"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { cn } from "@/lib/_utils";
import { TESTIMONIALS } from "./data";

export function HomeV2Testimonials() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  return (
    <section className="bg-white px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-[1600px] flex-col items-center">
        <div className="mx-auto max-w-[1056px] text-center">
          <h2 className="font-bold text-[#2C2C2C] text-[56px] leading-[1.1] tracking-[-0.02em] max-lg:text-4xl">
            Hear from our Tradies and DIYers
          </h2>
          <div aria-hidden className="h-6" />
          <p className="mx-auto max-w-[989px] font-normal text-[#3F474E] text-xl leading-[1.4]">
            Trusted by DIYers and professionals to deliver real results, every
            time.
          </p>
        </div>

        <div aria-hidden className="h-12" />

        {/* Figma: cards 485px, gap 56px, vertical dividers */}
        <Carousel
          className="w-full"
          opts={{ align: "start", loop: true }}
          setApi={setApi}
        >
          <CarouselContent className="-ml-0 lg:-ml-14">
            {TESTIMONIALS.map((item, index) => (
              <CarouselItem
                className="basis-[85%] pl-0 sm:basis-[70%] md:basis-[485px] lg:pl-14"
                key={item.id}
              >
                <div className="relative flex h-full">
                  {index > 0 && (
                    <div
                      aria-hidden
                      className="lg:-left-7 absolute top-0 bottom-0 left-0 hidden w-px bg-[#BCCAD4] lg:block"
                    />
                  )}
                  <blockquote className="flex w-full max-w-[485px] flex-col rounded-[30px]">
                    <svg
                      aria-hidden
                      className="shrink-0"
                      fill="none"
                      height="40"
                      viewBox="0 0 51 40"
                      width="51"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18.2101 0L14.786 18.9883H21.0117V40H0V18.677L5.60311 0H18.2101ZM47.4708 0L44.0467 18.9883H50.2724V40H29.2607V18.677L34.8638 0H47.4708Z"
                        fill="#0586D7"
                      />
                    </svg>

                    <div aria-hidden className="h-6" />

                    <p className="min-h-[140px] font-bold text-[#2C2C2C] text-xl leading-[1.4]">
                      {item.quote}
                    </p>

                    <div aria-hidden className="h-10" />

                    <footer className="mt-auto flex items-center gap-6">
                      <div className="relative size-[72px] shrink-0 overflow-hidden rounded-full bg-neutral-100">
                        <Image
                          alt={item.name}
                          className="object-cover"
                          fill
                          sizes="72px"
                          src={item.avatar}
                        />
                      </div>
                      <div>
                        <cite className="font-bold text-[#3F474E] text-lg not-italic leading-[1.4]">
                          {item.name}
                        </cite>
                        <div aria-hidden className="h-2" />
                        <p className="font-medium text-[#9FB3C8] text-base leading-[1.4]">
                          {item.role}
                        </p>
                      </div>
                    </footer>
                  </blockquote>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <div className="mt-10 flex justify-center gap-2">
          {TESTIMONIALS.map((item, index) => (
            <button
              aria-label={`Go to testimonial ${index + 1}`}
              className={cn(
                "size-2 rounded-full transition-colors",
                current === index
                  ? "bg-[#0586D7]"
                  : "bg-[#C4C4C4] hover:bg-[#9FB3C8]"
              )}
              key={item.id}
              onClick={() => api?.scrollTo(index)}
              type="button"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
